import React from 'react'
import {
  MetadataList,
  MetadataListItem
} from '../../legacy-design-system/metadata'
import { FormattedMessage } from 'react-intl'
import { ViolationIndicator } from '../applications-grid/violation-indicator'
import { MissingFieldIndicator } from '../applications-grid/missing-field-indicator'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Role } from '@contrast-security-inc/base-types'
import { metadataCss } from '../../legacy-design-system/text'
import gql from 'graphql-tag'
import { Query } from 'react-apollo'
import {
  appImportanceI18n,
  ApplicationImportance
} from '../application-importance'
import {
  CompliancePolicy,
  InvalidCustomField
} from '../applications-grid/shared'
import { applicationFragment } from '../applications-grid/fragments'
import { AgentLanguage } from '../agent-language'
import { Link } from '@contrast-security-inc/design-system-typography'

type CustomMetadataFieldType = 'STRING' | 'PERSON_OF_CONTACT' | 'NUMERIC'
type CustomMetadataSubfieldType = 'CONTACT_NAME' | 'EMAIL' | 'PHONE'

type LinkType = 'mailto' | 'tel'

interface CustomMetadataSubfield {
  readonly name: string
  readonly value: string
  readonly type: CustomMetadataSubfieldType
  readonly unique?: boolean
  readonly isOverriddenByUser: boolean
}

interface CustomMetadataField {
  readonly name: string
  readonly value: string
  readonly type: CustomMetadataFieldType
  readonly subfields?: CustomMetadataSubfield[]
  readonly unique?: boolean
  readonly isOverriddenByUser: boolean
}

export interface ApplicationMetadataProps {
  readonly applicationID: string
  readonly organizationID: string
  readonly organizationRole: Role
}

export const applicationQuery = gql`
  query GetApplication($organizationID: ID!, $applicationID: ID!) {
    application(
      organizationID: $organizationID
      applicationID: $applicationID
    ) {
      ...Application
    }
  }

  ${applicationFragment}
`

export interface Result {
  readonly application: {
    readonly url: string
    readonly language: string
    readonly importance: ApplicationImportance
    readonly violatedCompliancePolicies: CompliancePolicy[]
    readonly customMetadataFields: CustomMetadataField[]
    readonly invalidCustomMetadataFields: InvalidCustomField[]
  }
}

interface Vars {
  readonly organizationID: string
  readonly applicationID: string
}

function countMissingFields(invalidCustomMetadataFields: InvalidCustomField[]) {
  const invalidFields = invalidCustomMetadataFields.filter(
    (field) => field.validationType === 'invalid'
  ).length
  const missingFields = invalidCustomMetadataFields.filter(
    (field) => field.validationType === 'missing'
  ).length
  return missingFields + invalidFields
}

export class ApplicationMetadata extends React.Component<ApplicationMetadataProps> {
  private fieldsCounter = 0

  public render() {
    const { organizationID, applicationID, organizationRole } = this.props

    const policyViolationLabelCss = cx(
      metadataCss,
      css`
        color: ${theme.color.red50};
        display: inline-block;
        margin-left: 6px;
      `
    )

    const indicatorCss = css`
      display: inline-block;
    `

    const alertItemCss = css`
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 5px;
    `

    const missingFieldLabelCss = cx(
      metadataCss,
      css`
        color: ${theme.color.red50};
        display: inline-block;
        margin-right: 6px;
      `
    )

    const missingFieldItemCss = css`
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      @media (min-width: 768px) {
        &:not(:last-child)::after {
          margin-top: -5px;
          margin-left: 10px;
          margin-right: 10px;
          content: '|';
          color: ${theme.color.neutral40};
        }
      }
    `

    return (
      <Query<Result, Vars>
        query={applicationQuery}
        variables={{
          organizationID,
          applicationID
        }}
      >
        {({ loading, error, data }) => {
          if (error) {
            throw new Error(error.toString())
          }
          const application = data && data.application
          const agentLanguage = !!application
            ? AgentLanguage.fromKey(application.language)
            : undefined

          return loading ? (
            false
          ) : application ? (
            <MetadataList>
              <FormattedMessage id="title_url">
                {(text) => (
                  <MetadataListItem label={text} value={application.url} />
                )}
              </FormattedMessage>

              {!!agentLanguage && (
                <FormattedMessage id="title_programmingLanguage">
                  {(text) => (
                    <MetadataListItem
                      label={text}
                      value={agentLanguage.getDisplay()}
                    />
                  )}
                </FormattedMessage>
              )}

              <FormattedMessage id="title_importance_level">
                {(text) => (
                  <MetadataListItem
                    label={text}
                    value={
                      <FormattedMessage
                        id={appImportanceI18n[application.importance]}
                      />
                    }
                  />
                )}
              </FormattedMessage>

              {application.customMetadataFields &&
                !!application.customMetadataFields.length &&
                application.customMetadataFields.map((field) => {
                  if (!!field.subfields && field.subfields.length) {
                    // render all subfields
                    const { subfields } = field
                    const Subfields: React.ReactNode[] = []

                    if (field.type !== 'PERSON_OF_CONTACT') {
                      subfields.forEach((subfield) =>
                        Subfields.push(
                          <MetadataListItem
                            key={this.fieldsCounter++}
                            label={subfield.name}
                            value={subfield.value}
                            isOverriddenByUser={subfield.isOverriddenByUser}
                          />
                        )
                      )
                    } else {
                      const emailIndex = subfields.findIndex(
                        (subfield) => subfield.type === 'EMAIL'
                      )

                      subfields.forEach((subfield) => {
                        const { name, value, isOverriddenByUser } = subfield

                        switch (subfield.type) {
                          case 'CONTACT_NAME':
                            Subfields.push(
                              <MetadataListItem
                                label={name}
                                value={
                                  emailIndex !== -1 ? (
                                    <CustomMetadataLink
                                      linkType="mailto"
                                      linkValue={subfields[emailIndex].value}
                                      value={value}
                                    />
                                  ) : (
                                    value
                                  )
                                }
                                isOverriddenByUser={isOverriddenByUser}
                              />
                            )
                            break

                          case 'PHONE':
                            Subfields.push(
                              <MetadataListItem
                                label={name}
                                value={
                                  <CustomMetadataLink
                                    linkType="tel"
                                    linkValue={value}
                                    value={value}
                                  />
                                }
                                isOverriddenByUser={isOverriddenByUser}
                              />
                            )
                            break
                          case 'EMAIL':
                            break
                          default:
                            Subfields.push(
                              <MetadataListItem
                                key={this.fieldsCounter++}
                                label={subfield.name}
                                value={subfield.value}
                                isOverriddenByUser={isOverriddenByUser}
                              />
                            )
                        }
                      })
                    }

                    return Subfields
                  } else {
                    return (
                      <MetadataListItem
                        key={this.fieldsCounter++}
                        label={field.name}
                        value={field.value}
                        isOverriddenByUser={field.isOverriddenByUser}
                      />
                    )
                  }
                })}

              {!!application.invalidCustomMetadataFields &&
                application.invalidCustomMetadataFields.length > 0 && (
                  <li className={missingFieldItemCss}>
                    <FormattedMessage
                      id="title_missingField"
                      values={{
                        count: countMissingFields(
                          application.invalidCustomMetadataFields
                        )
                      }}
                    >
                      {(text) => (
                        <span className={alertItemCss}>
                          <span className={missingFieldLabelCss}>{text}</span>
                          <span className={indicatorCss}>
                            <MissingFieldIndicator
                              invalidCustomMetadataFields={
                                application.invalidCustomMetadataFields
                              }
                            />
                          </span>
                        </span>
                      )}
                    </FormattedMessage>
                  </li>
                )}

              {!!application.violatedCompliancePolicies &&
                application.violatedCompliancePolicies.length > 0 && (
                  <FormattedMessage id="title_policyViolation">
                    {(text) => (
                      <span className={alertItemCss}>
                        <span className={indicatorCss}>
                          <ViolationIndicator
                            organizationID={organizationID}
                            organizationRole={organizationRole}
                            violatedCompliancePolicies={
                              application.violatedCompliancePolicies
                            }
                          />
                        </span>
                        <span className={policyViolationLabelCss}>{text}</span>
                      </span>
                    )}
                  </FormattedMessage>
                )}
            </MetadataList>
          ) : (
            false
          )
        }}
      </Query>
    )
  }
}

interface CustomMetadataLinkProps {
  readonly linkValue: string
  readonly value: string
  readonly linkType: LinkType
}

function CustomMetadataLink({
  linkValue,
  value,
  linkType
}: CustomMetadataLinkProps) {
  return (
    <Link
      className={css`
        font-size: 12px;
      `}
      href={`${linkType}:${linkValue}`}
      children={value}
    />
  )
}
